<template>
    <section v-if="items.length" class="">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader title="Sponsors" prefix="Event!" :icon="Handshake" :options="layoutOptions" v-model="mode" />
    
            <div class="space-y-10">
                <div v-for="group in groups" :key="group.tier">
                    <div v-if="group.tier" class="flex items-center gap-3 mb-4">
                        <h3 class="text-xs font-bold uppercase tracking-widest text-body/80">{{ group.tier }}</h3>
                        <div class="h-px flex-1 bg-app"></div>
                    </div>
    
                    <!-- Logo wall (default) -->
                    <div v-if="mode === 'logos'" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        <component :is="s.website ? 'a' : 'div'" v-for="(s, i) in group.items" :key="i"
                            :href="s.website || undefined" :target="s.website ? '_blank' : undefined" rel="noopener"
                            class="group relative flex flex-col items-center justify-center gap-3 rounded-2xl border border-app bg-card/70 backdrop-blur-2xl p-6 shadow-lg transition-all hover:-translate-y-1 hover:shadow-brand-500/10">
                            <div class="h-16 w-16 rounded-xl overflow-hidden border border-app bg-white flex items-center justify-center">
                                <img v-if="s.logoUrl" :src="s.logoUrl" :alt="s.name" class="h-full w-full object-contain p-1.5" />
                                <span v-else class="text-brand-500 font-black">{{ initials(s.name) }}</span>
                            </div>
                            <div class="text-center min-w-0 w-full">
                                <h4 class="text-header font-bold text-sm truncate group-hover:text-brand-500 transition-colors">
                                    {{ s.name }}</h4>
                            </div>
                            <ExternalLink v-if="s.website"
                                class="absolute top-3 right-3 h-3.5 w-3.5 text-body/80 group-hover:text-brand-500 transition-colors" />
                        </component>
                    </div>
    
                    <!-- List -->
                    <div v-else-if="mode === 'list'"
                        class="rounded-2xl border border-app bg-card/70 backdrop-blur-2xl divide-y divide-app">
                        <component :is="s.website ? 'a' : 'div'" v-for="(s, i) in group.items" :key="i"
                            :href="s.website || undefined" :target="s.website ? '_blank' : undefined" rel="noopener"
                            class="group flex items-center gap-4 p-4 sm:p-5">
                            <div class="h-11 w-11 shrink-0 rounded-lg overflow-hidden border border-app bg-white flex items-center justify-center">
                                <img v-if="s.logoUrl" :src="s.logoUrl" :alt="s.name" class="h-full w-full object-contain p-1" />
                                <span v-else class="text-brand-500 font-black text-xs">{{ initials(s.name) }}</span>
                            </div>
                            <div class="min-w-0 flex-1">
                                <h4 class="text-header font-bold leading-snug truncate group-hover:text-brand-500 transition-colors">
                                    {{ s.name }}</h4>
                                <p v-if="s.about" class="text-body/80 text-xs truncate">{{ s.about }}</p>
                            </div>
                            <ExternalLink v-if="s.website"
                                class="h-4 w-4 shrink-0 text-body/80 group-hover:text-brand-500 transition-colors" />
                        </component>
                    </div>
    
                    <!-- Card -->
                    <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <component :is="s.website ? 'a' : 'div'" v-for="(s, i) in group.items" :key="i"
                            :href="s.website || undefined" :target="s.website ? '_blank' : undefined" rel="noopener"
                            class="group flex flex-col rounded-2xl border border-app bg-card/70 backdrop-blur-2xl p-5 shadow-lg transition-all hover:-translate-y-1 hover:shadow-brand-500/10">
                            <div class="flex items-center gap-4 mb-3">
                                <div class="h-12 w-12 shrink-0 rounded-xl overflow-hidden border border-app bg-white flex items-center justify-center">
                                    <img v-if="s.logoUrl" :src="s.logoUrl" :alt="s.name" class="h-full w-full object-contain p-1" />
                                    <span v-else class="text-brand-500 font-black text-sm">{{ initials(s.name) }}</span>
                                </div>
                                <div class="min-w-0">
                                    <h4 class="text-header font-bold leading-snug truncate group-hover:text-brand-500 transition-colors">
                                        {{ s.name }}</h4>
                                    <p v-if="s.tier" class="text-body/80 text-xs font-semibold mt-0.5 truncate">{{ s.tier }}</p>
                                </div>
                            </div>
                            <p v-if="s.about" class="text-body text-sm line-clamp-2 mb-3">{{ s.about }}</p>
                            <div v-if="s.website" class="mt-auto flex items-center gap-2">
                                <ExternalLink class="h-3.5 w-3.5 text-body/80 group-hover:text-brand-500 transition-colors" />
                            </div>
                        </component>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Handshake, ExternalLink, LayoutGrid, List, Grid3x3 } from 'lucide-vue-next';
import { EventSponsor } from '~/types';
import SectionHeader from './SectionHeader.vue';

type SponsorLayout = 'logos' | 'card' | 'list';

const props = withDefaults(
    defineProps<{ items: EventSponsor[]; variant?: SponsorLayout }>(),
    { variant: 'logos' }
);

const mode = ref<SponsorLayout>(props.variant);

const layoutOptions: { value: SponsorLayout; label: string; icon: any }[] = [
    { value: 'logos', label: 'Logo wall', icon: Grid3x3 },
    { value: 'card', label: 'Cards', icon: LayoutGrid },
    { value: 'list', label: 'List', icon: List },
];

const groups = computed(() => {
    const map = new Map<string, EventSponsor[]>();
    for (const s of props.items) {
        const tier = s.tier || '';
        if (!map.has(tier)) map.set(tier, []);
        map.get(tier)!.push(s);
    }
    for (const list of map.values()) {
        list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    return Array.from(map.entries()).map(([tier, list]) => ({ tier, items: list }));
});

const initials = (name: string) =>
    (name || '')
        .split(' ')
        .map((p) => p[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase();
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.bg-app {
    background-color: rgb(var(--core-app-border));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.text-body {
    color: rgb(var(--core-body-text));
}
</style>
